/**
 * The game's audio, end to end.
 *
 * Sound is a **client** concern and nothing about it crosses the wire. The
 * server says what happened; this decides what that sounds like. There is no
 * audio in the protocol, none in the simulation, and a player with the volume
 * at zero is playing exactly the same game as one with it up.
 *
 * ### Nothing here touches the tank-to-camera path
 *
 * `CLAUDE.md`'s smoothness invariants forbid anything frame-rate dependent
 * between the tank and the camera, and audio obeys that by construction rather
 * than by care: `update()` takes **no `dt`**, holds no per-frame state, and is
 * called from the end of the render loop with values that have already been
 * computed and already been used. It reads; it never writes back. Every
 * continuous parameter it sets is glided by Web Audio on the *audio* clock
 * (see `synth.ts`), so the frame rate cannot get into the result even
 * indirectly.
 *
 * ### One context per tab, and it starts blocked
 *
 * A browser will not start an `AudioContext` before a user gesture, and
 * creating one per screen would exhaust the handful a tab is allowed. So there
 * is a single lazy instance, shared by the garage and the arena, and the first
 * click or keypress anywhere resumes it. Everything degrades to a no-op if the
 * context cannot be created or refuses to resume — a browser without Web Audio,
 * or one that never gets its gesture, plays the game silently rather than
 * throwing or filling the console.
 */
import { watch } from 'vue';

import {
  audioMuted,
  currentMasterVolume,
  masterVolume,
  musicMuted,
  musicVolume,
} from '@/state/settings';
import type { WeaponArchetype } from '@shared/weapons';
import {
  COALESCE_MS,
  CUES,
  MAX_STACK,
  MAX_TRACK_LOOPS,
  MAX_VOICES,
  MAX_AUDIBLE_DISTANCE,
  NOMINAL_TOP_SPEED,
  VoiceLimiter,
  distanceGain,
  fireCue,
  SAMPLE_IDS,
  sampleFor,
  stereoPan,
  trackLoopGain,
  type CueId,
} from './policy';
import {
  MUSIC_MUTE_LABEL,
  MusicBed,
  currentMusicGain,
  installMusicMuteKey,
  type MusicScene,
} from './music';
import { SampleBank, TrackBed, playSample } from './samples';
import { ChargeVoice, EngineVoice, playVoice } from './synth';

/** Where a cue happens. Omitted entirely for something that happens *to you*. */
export interface CueOptions {
  /** World position, for distance rolloff and stereo placement. */
  x?: number;
  z?: number;
  /** Extra multiplier on top of the cue's own gain. */
  gain?: number;
}

/** What the render loop tells the audio each frame. All of it already computed. */
export interface AudioFrame {
  /** Where the listener is — the tank, as drawn. */
  x: number;
  z: number;
  /** The direction the player is *facing*, radians. */
  yaw: number;
  /** Ground speed as a fraction of this tank's own top speed, 0–1. */
  speedFraction: number;
  /** Throttle axis magnitude, 0–1. */
  throttle: number;
  alive: boolean;
  /** Capacitor charge, 0–1. Always 0 on a gun that does not charge. */
  charge: number;
}

/** Elements a click on which is interface rather than gameplay. */
const UI_SELECTOR = 'button, [role="button"], a[href]';

/** One tank's contribution to the track bed, as the render loop reports it. */
interface TrackRequest {
  id: string;
  x: number;
  z: number;
  /** Ground speed over that tank's own top speed, 0–1. */
  speedFraction: number;
  /** Computed in `commitTracks`, where the listener is fresh. */
  gain: number;
  pan: number;
}

export class GameAudio {
  private ctx: AudioContext | null = null;
  /** Player volume. Everything audible passes through it, so mute is one node. */
  private bus: GainNode | null = null;
  /**
   * Catches the sum of a busy firefight.
   *
   * Uncorrelated voices add, and twelve of them can exceed full scale even with
   * every individual cue well behaved. Without this the loud moments — which
   * are the ones that matter — are the ones that distort.
   */
  private compressor: DynamicsCompressorNode | null = null;
  /** DEV only: lets a browser test measure what actually reached the output. */
  private analyser: AnalyserNode | null = null;
  // Explicitly backed by a plain ArrayBuffer: `getFloatTimeDomainData` will not
  // accept a view that might sit on a SharedArrayBuffer.
  private analyserBuffer: Float32Array<ArrayBuffer> | null = null;

  private engine: EngineVoice | null = null;
  private charge: ChargeVoice | null = null;

  /**
   * The recordings, and the looping track rumble built out of one of them.
   *
   * Both are additions to the synthesised graph rather than replacements for
   * any of it: `sampleFor` decides which cues have a recording, everything else
   * stays synthesised, and a cue whose recording has not loaded — or never
   * will — plays its `VoiceSpec` instead. See `docs/audio.md`.
   */
  private readonly bank = new SampleBank();
  private tracks: TrackBed | null = null;
  private inArena = false;

  /**
   * The music bed and its own gain path.
   *
   * **`musicBus` is wired straight to the destination**, parallel to `bus` and
   * the compressor rather than through either. Two reasons, both in
   * `music.ts` at length: the voice limiter cannot account for a source that
   * never ends without either starving it or being starved by it, and a
   * continuous bed fed into the cue compressor would keep it engaged for the
   * whole match and spend the headroom the cues were tuned against.
   *
   * It is also deliberately **not** connected to the DEV analyser. `peak` exists
   * to answer "did a cue actually reach the output", and a bed playing under it
   * would mean that number never reads zero again — a measurement several
   * existing assertions rest on. The bed answers for itself through
   * `__sowAudio().music` instead.
   */
  private musicBus: GainNode | null = null;
  private music: MusicBed | null = null;
  private detachMusicKey: (() => void) | null = null;
  /** What the app has asked for, kept so a re-entry cannot lose it. */
  private musicScene: MusicScene = 'none';

  private readonly limiter = new VoiceLimiter();
  private listener = { x: 0, z: 0, yaw: 0 };

  /**
   * This frame's track sources, reused rather than rebuilt.
   *
   * The render loop reports every tank it drew, once per frame, so this is
   * allocated to the arena's maximum on the first frame and then written
   * through for the life of the match — a per-frame array of a dozen objects is
   * garbage during combat for no gain.
   */
  private readonly trackRequests: TrackRequest[] = [];
  private trackCount = 0;
  private readonly trackOrder: number[] = [];
  private readonly trackSeen = new Set<string>();

  /** Dev counters. Cheap, and the only way to verify audio without listening. */
  private nodesCreated = 0;
  private readonly cueCounts = new Map<CueId, number>();
  private outOfRange = 0;
  private suppressed = 0;
  /** Voices that came from a recording, and cues that wanted one and had none. */
  private samplesPlayed = 0;
  private sampleFallbacks = 0;

  private unlockAttached = false;
  private globalsAttached = false;
  /** True once the context has actually reached `running`. */
  private unlocked = false;

  constructor() {
    try {
      const Ctor =
        window.AudioContext ??
        (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!Ctor) return;
      this.ctx = new Ctor();
    } catch {
      // No Web Audio, or a policy that refuses to construct one. The game is
      // playable without sound and must not care.
      this.ctx = null;
      return;
    }

    const ctx = this.ctx;
    this.bus = ctx.createGain();
    this.compressor = ctx.createDynamicsCompressor();
    this.compressor.threshold.value = -12;
    this.compressor.knee.value = 12;
    this.compressor.ratio.value = 6;
    this.compressor.attack.value = 0.004;
    this.compressor.release.value = 0.18;
    this.bus.connect(this.compressor);

    if (import.meta.env.DEV) {
      this.analyser = ctx.createAnalyser();
      this.analyser.fftSize = 2048;
      this.analyserBuffer = new Float32Array(this.analyser.fftSize);
      this.compressor.connect(this.analyser);
      this.analyser.connect(ctx.destination);
    } else {
      this.compressor.connect(ctx.destination);
    }

    // The bed's own path, beside the cue bus rather than inside it. See the
    // field's comment for why it bypasses both the compressor and the analyser.
    this.musicBus = ctx.createGain();
    this.musicBus.gain.value = 0;
    this.musicBus.connect(ctx.destination);
    this.music = new MusicBed(ctx, this.musicBus);

    // Volume is the player's, and it is read from the same store the aim
    // sensitivity lives in. `immediate` so the stored value is on the bus before
    // the first sound rather than after it.
    watch([masterVolume, audioMuted], () => this.applyVolume(), { immediate: true });
    // The music path answers to four settings: the master pair, because muting
    // the game must mute the music, and its own pair. `refresh` as well as
    // `applyGain` because a mute tears the bed down rather than playing it at
    // zero — see `musicShouldPlay`.
    watch(
      [masterVolume, audioMuted, musicVolume, musicMuted],
      () => {
        this.music?.applyGain();
        this.music?.refresh();
      },
      { immediate: true },
    );

    this.unlocked = ctx.state === 'running';
    this.installUnlock();
    if (import.meta.env.DEV) this.installProbe();
  }

  // ─── Gesture unlock ────────────────────────────────────────────────────────

  /**
   * Resumes the context on the first gesture, then stops listening.
   *
   * Capture-phase and passive, so it cannot interfere with anything the game
   * does with the same event — firing is a click on the canvas, and the shot
   * must not be delayed or swallowed by the audio waking up.
   */
  private installUnlock(): void {
    if (this.unlockAttached || !this.ctx) return;
    this.unlockAttached = true;
    const options: AddEventListenerOptions = { capture: true, passive: true };
    for (const type of ['pointerdown', 'keydown', 'touchstart'] as const) {
      window.addEventListener(type, this.onGesture, options);
    }
  }

  private readonly onGesture = (): void => {
    void this.resume();
  };

  /**
   * Whether the context is actually producing sound.
   *
   * A getter rather than a `ctx.state === 'running'` at each call site, because
   * the compiler narrows that property and then refuses the *next* comparison
   * against `'running'` in the same function as impossible — which it is not,
   * since `resume()` changes it. Reading it through here has no narrowing to
   * carry, and gives the three callers one spelling of the question.
   */
  private get running(): boolean {
    return this.ctx?.state === 'running';
  }

  /** Never throws and never logs: a refusal is a quiet game, not an error. */
  async resume(): Promise<void> {
    const ctx = this.ctx;
    if (!ctx) return;
    try {
      if (!this.running) await ctx.resume();
    } catch {
      // Autoplay policy said no. It will be asked again on the next gesture.
      return;
    }
    if (this.running) this.onUnlocked();
  }

  private onUnlocked(): void {
    if (this.unlocked) return;
    this.unlocked = true;
    const options: AddEventListenerOptions = { capture: true };
    for (const type of ['pointerdown', 'keydown', 'touchstart'] as const) {
      window.removeEventListener(type, this.onGesture, options);
    }
    // This is the whole of the music's autoplay handling: the bed is asked for
    // as soon as the app knows which screen it is on, records that it could not
    // start, and starts here instead — on the first frame the browser actually
    // allows sound. No caller has to know whether a gesture has happened.
    this.music?.contextUnlocked();
  }

  // ─── Interface clicks ──────────────────────────────────────────────────────

  /**
   * One delegated listener for every button in the app.
   *
   * Delegated rather than a `@click` in each component on purpose: the garage
   * has twenty-odd buttons across six files, and a click sound is a property of
   * *being a button*, not of any one of them. A new screen gets it for free, and
   * nothing in `garage/` had to be edited to gain it.
   */
  installGlobals(): void {
    if (this.globalsAttached) return;
    this.globalsAttached = true;
    document.addEventListener('pointerdown', this.onDocumentPointerDown, {
      capture: true,
      passive: true,
    });
    // The mute key, attached once for the life of the tab. It lives here rather
    // than in `game/input.ts` because that is the *arena's* input source and is
    // torn down with the scene, and a mute you cannot reach in the garage or on
    // the sign-in form is a mute that is missing when somebody walks in. See
    // `installMusicMuteKey`.
    this.detachMusicKey ??= installMusicMuteKey();
    // The garage is the landing screen, so it is the bed the app opens on. This
    // is only a *request*: nothing is fetched while the music is muted and
    // nothing plays until the context is allowed to.
    this.setMusicScene('garage');
  }

  // ─── Music ─────────────────────────────────────────────────────────────────

  /**
   * Which bed should be playing.
   *
   * Called with `'battle'` from `enterArena` and `'garage'` from `leaveArena`,
   * so `arena-scene.ts` needed no edit at all — it already tells the audio when
   * a match begins and ends, and that is exactly the two moments the music
   * changes. The loading screen deliberately gets no scene of its own: it is the
   * transition, `enterArena` runs while it is still up, and a crossfade under a
   * full-screen painting is the best moment in the game to spend one.
   */
  setMusicScene(scene: MusicScene): void {
    this.musicScene = scene;
    this.music?.select(scene);
  }

  private readonly onDocumentPointerDown = (event: Event): void => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    // Firing the gun is a click on the canvas, which is not a button — so the
    // trigger never also clicks the interface.
    if (!target.closest(UI_SELECTOR)) return;
    this.cue('ui:click');
  };

  // ─── Playing ───────────────────────────────────────────────────────────────

  /**
   * Plays one cue, if the voice cap and the distance allow it.
   *
   * Position is optional: a cue with no position is something happening *to*
   * the listener — taking a hit, clicking a button — and is played centred and
   * unattenuated, because it is already here.
   */
  cue(id: CueId, options: CueOptions = {}): void {
    const ctx = this.ctx;
    const bus = this.bus;
    const spec = CUES[id];
    if (!ctx || !bus || !spec) return;
    // Nodes scheduled on a suspended context queue up and all fire at once when
    // it resumes, which is a wall of noise on the first click. Count and drop.
    if (!this.running) {
      this.suppressed += 1;
      return;
    }

    // Which of the two voices this cue is going to be, decided before anything
    // is scheduled — because the limiter has to be told the right occupancy, and
    // a recorded shot and its synthesised stand-in do not have the same one. For
    // a synthesised cue that is its whole duration; for a recording it is
    // `hold`, the loud part rather than the reverb tail. See `SampleSpec.hold`.
    const recorded = sampleFor(id);
    const buffer = recorded ? this.bank.get(recorded.sample) : null;
    if (recorded && !buffer) this.sampleFallbacks += 1;
    const occupancy = buffer && recorded ? recorded.hold : spec.duration;
    const baseGain = buffer && recorded ? recorded.gain : spec.gain;

    let placement = 1;
    let pan = 0;
    if (options.x !== undefined && options.z !== undefined) {
      const dx = options.x - this.listener.x;
      const dz = options.z - this.listener.z;
      const distance = Math.hypot(dx, dz);
      if (distance >= MAX_AUDIBLE_DISTANCE) {
        this.outOfRange += 1;
        return;
      }
      placement = distanceGain(distance);
      pan = stereoPan(dx, dz, this.listener.yaw);
    }

    // The cap is asked *after* distance, so a shot too far away to hear never
    // costs a voice that a nearby one could have used — and it is the same cap
    // either way. A sample path with its own budget would let a volley of
    // recorded reports sum straight past the ceiling the synthesised ones
    // respect, which is the whole reason the limiter exists.
    const allowance = this.limiter.request(id, performance.now(), occupancy);
    if (allowance <= 0) return;

    const gain = baseGain * placement * allowance * (options.gain ?? 1);
    if (gain <= 0.0005) return;

    if (buffer && recorded) {
      this.nodesCreated += playSample(ctx, bus, buffer, recorded, gain, pan);
      this.samplesPlayed += 1;
    } else {
      this.nodesCreated += playVoice(ctx, bus, spec, gain, pan);
    }
    this.cueCounts.set(id, (this.cueCounts.get(id) ?? 0) + 1);
  }

  /** The cue for a weapon, by archetype. A scattergun and a railgun differ here. */
  fired(archetype: WeaponArchetype, x: number, z: number): void {
    this.cue(fireCue(archetype), { x, z });
  }

  // ─── Continuous sound ──────────────────────────────────────────────────────

  /** Builds the engine and the capacitor whine. Called when a match starts. */
  enterArena(): void {
    const ctx = this.ctx;
    const bus = this.bus;
    if (!ctx || !bus || this.engine) return;
    this.inArena = true;
    this.engine = new EngineVoice(ctx, bus);
    this.charge = new ChargeVoice(ctx, bus);
    this.nodesCreated += this.engine.nodeCount + 4;
    // The tenser bed, crossfaded in on its own path. This runs while the loading
    // screen is still up, which is where the switch belongs.
    this.setMusicScene('battle');
    // Fetched here rather than at construction so a player who never deploys
    // pays nothing for them, and here rather than on the first shot because the
    // loading screen is already up and has its own floor — 156 KB has landed and
    // decoded long before the first round leaves a barrel. Deliberately not
    // awaited: a slow or missing download must not delay the arena by a frame,
    // and the cues fall back to the synth until it arrives.
    this.bank.loadAll(ctx);
  }

  /**
   * Tears them down again, and leaves the context alive.
   *
   * The garage still wants its clicks, and a tab is allowed only a handful of
   * contexts — closing one per match would run out.
   */
  leaveArena(): void {
    this.inArena = false;
    this.engine?.dispose();
    this.engine = null;
    this.charge?.dispose();
    this.charge = null;
    this.tracks?.dispose();
    this.tracks = null;
    this.trackCount = 0;
    this.trackSeen.clear();
    // Back to the calm bed. The music bed itself is *not* disposed — it is one
    // node and two decoded buffers shared by every screen, and tearing it down
    // per sortie would re-decode a megabyte of PCM on the way back to the
    // garage. Same reasoning as the sample bank below.
    this.setMusicScene('garage');
    // The decoded buffers stay. They are the same five for every match, and
    // re-fetching them per deploy would be a download the browser cache would
    // answer and a decode it would not.
  }

  // ─── The track bed ─────────────────────────────────────────────────────────

  /**
   * Reports one tank the render loop drew, for the recorded track rumble.
   *
   * Called once per tank per frame, **before** `update()`, which is what
   * consumes them. Positions and speeds only: distance, pan and the budget are
   * all resolved in `commitTracks`, where the listener is the one this frame
   * actually placed rather than the one the last frame left behind.
   */
  trackSource(id: string, x: number, z: number, speedFraction: number, alive: boolean): void {
    if (!this.inArena) return;
    const slot = this.trackRequests[this.trackCount] ?? {
      id,
      x,
      z,
      speedFraction: 0,
      gain: 0,
      pan: 0,
    };
    slot.id = id;
    slot.x = x;
    slot.z = z;
    // A wreck does not drive, for the same reason it does not idle.
    slot.speedFraction = alive ? speedFraction : 0;
    this.trackRequests[this.trackCount] = slot;
    this.trackCount += 1;
  }

  /**
   * Turns this frame's reported tanks into loop gains.
   *
   * The budget is spent on the **loudest** sources rather than the nearest,
   * because the two differ: a tank flat out at 40 m is more worth hearing than
   * one creeping at 15 m, and gain already carries both facts. Everything past
   * the budget is set to zero rather than dropped, so a tank that falls out of
   * the top six fades instead of stopping.
   */
  private commitTracks(): void {
    const ctx = this.ctx;
    const bus = this.bus;
    const count = this.trackCount;
    this.trackCount = 0;
    if (!ctx || !bus) return;
    // Nothing reported and nothing running: the garage calls `update` too, and
    // a bed with no tanks in it is a graph node built for a screen that has no
    // tanks on it.
    if (count === 0 && !this.tracks) return;

    const buffer = this.bank.get('move-loop');
    if (!buffer) return;
    // Built on the first frame a decoded loop exists, which is normally the
    // first frame of the match and — if the download was slow, or never
    // finished — is simply never. The synthesised engine covers both.
    this.tracks ??= new TrackBed(ctx, bus, buffer);
    const tracks = this.tracks;

    this.trackOrder.length = count;
    for (let i = 0; i < count; i += 1) {
      const request = this.trackRequests[i];
      const dx = request.x - this.listener.x;
      const dz = request.z - this.listener.z;
      request.gain = trackLoopGain(request.speedFraction, Math.hypot(dx, dz));
      request.pan = stereoPan(dx, dz, this.listener.yaw);
      this.trackOrder[i] = i;
    }
    this.trackOrder.sort((a, b) => this.trackRequests[b].gain - this.trackRequests[a].gain);

    this.trackSeen.clear();
    for (let rank = 0; rank < count; rank += 1) {
      const request = this.trackRequests[this.trackOrder[rank]];
      this.trackSeen.add(request.id);
      tracks.set(request.id, rank < MAX_TRACK_LOOPS ? request.gain : 0, request.pan);
    }
    tracks.retain(this.trackSeen);
  }

  /**
   * One call from the end of the render loop.
   *
   * Takes no `dt` and returns nothing, which is the point: there is no way for
   * this to introduce frame-rate-dependent behaviour into anything, because it
   * cannot observe the frame rate and nothing downstream reads its output.
   */
  update(frame: AudioFrame): void {
    this.listener.x = frame.x;
    this.listener.z = frame.z;
    this.listener.yaw = frame.yaw;
    if (!this.running) {
      // Still consumed, or a frame's worth of sources would be carried into the
      // next one and the same tank counted twice.
      this.trackCount = 0;
      return;
    }
    this.engine?.set(frame.speedFraction, frame.throttle, frame.alive);
    this.charge?.set(frame.alive ? frame.charge : 0);
    // Last, and reading the listener this call has just placed. The rumble is a
    // layer *under* the synthesised engine, never a replacement for it: the
    // engine note is parametric and answers to the throttle, and this carries
    // the tracks, where volume rather than pitch is what speed changes.
    this.commitTracks();
  }

  // ─── Volume ────────────────────────────────────────────────────────────────

  private applyVolume(): void {
    const bus = this.bus;
    const ctx = this.ctx;
    if (!bus || !ctx) return;
    const wanted = currentMasterVolume();
    // Ramped over a few milliseconds rather than assigned: a step change in gain
    // while sound is passing through is a click, and dragging the slider would
    // produce one per pixel.
    bus.gain.setTargetAtTime(wanted, ctx.currentTime, 0.015);
    // The ramp is asymptotic and a test — reasonably — asks whether mute is
    // actually zero, so land the value exactly a moment later too.
    bus.gain.setValueAtTime(wanted, ctx.currentTime + 0.08);
  }

  // ─── Dev probe ─────────────────────────────────────────────────────────────

  /**
   * `window.__sowAudio()`.
   *
   * Audio cannot be verified by looking at a screenshot and — in a headless
   * browser with no audio device — cannot be verified by listening either. So
   * everything worth asserting is a number: that the graph exists, that firing
   * built nodes, that the cap held under a volley, and that muting really put
   * zero on the bus. `peak` is measured off the actual output, so it answers
   * "did any signal come out" rather than "did we intend for some to".
   */
  private installProbe(): void {
    (window as unknown as Record<string, unknown>).__sowAudio = () => ({
      supported: this.ctx !== null,
      contextState: this.ctx?.state ?? 'none',
      sampleRate: this.ctx?.sampleRate ?? 0,
      unlocked: this.unlocked,
      volume: masterVolume.value,
      muted: audioMuted.value,
      busGain: this.bus?.gain.value ?? 0,
      graph: {
        bus: this.bus !== null,
        compressor: this.compressor !== null,
        engine: this.engine !== null,
        charge: this.charge !== null,
        tracks: this.tracks !== null,
        /** The bed's own gain node. Its presence is what "outside the bus" means. */
        musicBus: this.musicBus !== null,
      },
      nodesCreated: this.nodesCreated,
      voices: {
        active: this.limiter.activeCount(performance.now()),
        played: this.limiter.played,
        droppedByStack: this.limiter.droppedByStack,
        droppedByCap: this.limiter.droppedByCap,
        outOfRange: this.outOfRange,
        suppressed: this.suppressed,
        maxVoices: MAX_VOICES,
        maxStack: MAX_STACK,
        coalesceMs: COALESCE_MS,
      },
      /**
       * The recorded half.
       *
       * `played` counts voices that came out of a file and `fellBackToSynth`
       * counts cues that wanted one and did not get it, so the two together
       * answer the question a missing asset actually raises: did the game go
       * quiet, or did it play the synthesised cue instead? Silence would show
       * as neither counter moving.
       */
      samples: {
        ...this.bank.report(),
        // Named, so a case can check "every recording loaded" without keeping
        // its own copy of the list — which went stale the first time one was
        // added and reported "6 of 5 recordings failed".
        ids: [...SAMPLE_IDS],
        played: this.samplesPlayed,
        fellBackToSynth: this.sampleFallbacks,
        tracks: {
          voices: this.tracks?.size ?? 0,
          audible: this.tracks?.audible() ?? 0,
          max: MAX_TRACK_LOOPS,
          nominalTopSpeed: NOMINAL_TOP_SPEED,
        },
      },
      /**
       * The bed, countable.
       *
       * Four questions, and each of them is a different way for this feature to
       * be broken while looking fine: **is it playing** (`playing` names the
       * track, `null` means nothing is sounding), **is it muted** (`muted` and
       * `masterMuted`, which are two different reasons for the same silence),
       * **how loud is it** (`gain` read off the node, beside the `targetGain`
       * the settings ask for, so a bus that did not take the value is visible),
       * and **did it load or fall back** — `loaded` against `fellSilent`, which
       * is the pair that separates a bed that never arrived from a bed nobody
       * asked for. `blockedByAutoplay` is the third kind of silence.
       *
       * `scene` is what the app asked for and `wanted` is the track that maps
       * to; they differ only if the mapping is wrong, which is worth being able
       * to see.
       */
      music: {
        scene: this.musicScene,
        muted: musicMuted.value,
        masterMuted: audioMuted.value,
        volume: musicVolume.value,
        gain: this.musicBus?.gain.value ?? 0,
        targetGain: currentMusicGain(),
        muteKey: MUSIC_MUTE_LABEL,
        ...(this.music?.report() ?? {
          ids: [],
          wanted: null,
          playing: null,
          loaded: 0,
          failed: 0,
          pending: 0,
          buffers: {},
          failures: {},
          lengthMismatches: {},
          started: 0,
          switched: 0,
          blockedByAutoplay: 0,
          fellSilent: 0,
          silentTracks: [],
          outsideLimiter: true as const,
        }),
      },
      cues: Object.fromEntries(this.cueCounts),
      engineState: this.engine
        ? {
            rpm: this.engine.rpm,
            throttle: this.engine.throttle,
            speed: this.engine.speed,
            level: this.engine.level,
          }
        : null,
      chargeState: this.charge ? this.charge.fraction : null,
      listener: { ...this.listener },
      peak: this.peak(),
    });
  }

  /** Largest absolute sample in the analyser's current window. DEV only. */
  private peak(): number {
    const analyser = this.analyser;
    const buffer = this.analyserBuffer;
    if (!analyser || !buffer) return -1;
    analyser.getFloatTimeDomainData(buffer);
    let peak = 0;
    for (let i = 0; i < buffer.length; i += 1) {
      const value = Math.abs(buffer[i]);
      if (value > peak) peak = value;
    }
    return peak;
  }
}

let instance: GameAudio | null = null;

/** The one audio instance for the tab, built on first use. */
export function gameAudio(): GameAudio {
  return (instance ??= new GameAudio());
}

/**
 * Starts audio listening for gestures and interface clicks. Called once, from
 * `main.ts`, so the garage has its clicks before any match exists.
 */
export function initGameAudio(): void {
  gameAudio().installGlobals();
}

export type { CueId } from './policy';
